// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { QueryClient, useIsFetching } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext, createRoute, createRouter, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { useEffect } from "react";

import { AppNav } from "./components/AppNav";
import { Page } from "./components/PlaceholderPage";
import { ErrorBanner, Skeleton } from "./components/states";
import { ApiError } from "./lib/api";
import { t } from "./lib/messages";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 4xx won't change by asking again; network errors and 5xx get one more try.
      retry: (count, error) => count < 1 && !(error instanceof ApiError && error.status >= 400 && error.status < 500),
      refetchOnWindowFocus: false,
    },
  },
});

/** An in-app path (not another site, not a hash or mailto link). */
function inAppPath(href: string | null): string | null {
  if (!href?.startsWith("/") || href.startsWith("//")) return null;
  return href;
}

/**
 * Routes in-app links through the router instead of reloading the iframe: App Bridge fires
 * shopify:navigate for s-app-nav and Polaris links; plain clicks on s-link/s-button/a with
 * a relative href are caught as well (the same event is a no-op when both fire).
 */
function useClientNavigation() {
  const router = useRouter();
  useEffect(() => {
    const go = (href: string) => {
      const here = window.location.pathname + window.location.search;
      if (href !== here) void router.navigate({ href });
    };
    const onShopifyNavigate = (event: Event) => {
      const target = event.target instanceof Element ? event.target : null;
      const href = inAppPath(target?.getAttribute("href") ?? null);
      if (href) go(href);
    };
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest("s-link[href], s-button[href], s-clickable[href], a[href]") : null;
      if (!link || link.getAttribute("target") === "_blank" || link.closest("s-app-nav")) return;
      const href = inAppPath(link.getAttribute("href"));
      if (!href) return;
      event.preventDefault();
      go(href);
    };
    document.addEventListener("shopify:navigate", onShopifyNavigate);
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("shopify:navigate", onShopifyNavigate);
      document.removeEventListener("click", onClick);
    };
  }, [router]);
}

/** Shows Shopify's loading bar while any request is in flight. */
function useAdminLoadingBar() {
  const fetching = useIsFetching() > 0;
  useEffect(() => {
    try {
      window.shopify.loading(fetching);
    } catch {
      // App Bridge not loaded (opened outside the admin): nothing to show.
    }
  }, [fetching]);
}

function Root() {
  useClientNavigation();
  useAdminLoadingBar();
  return (
    <>
      <AppNav />
      <Outlet />
    </>
  );
}

/** A render error anywhere below the root: an inline, persistent banner with a retry. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <Page heading={t("error.heading")}>
      <ErrorBanner
        error={error}
        onRetry={() => {
          reset();
          void router.invalidate();
        }}
      />
    </Page>
  );
}

function NotFound() {
  return (
    <Page heading={t("page.notFound")} back={{ href: "/", label: t("page.home") }}>
      <s-section>
        <s-paragraph>{t("notFound.body")}</s-paragraph>
        <s-link href="/">{t("notFound.home")}</s-link>
      </s-section>
    </Page>
  );
}

function Pending() {
  return (
    <s-page>
      <Skeleton />
    </s-page>
  );
}

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: Root,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

// Each page is its own lazily loaded chunk (SH8-T02: keep the main bundle small). A page
// file exports `Route = createLazyRoute(path)({ component })`.
const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: "/" }).lazy(() => import("./routes/home").then((m) => m.Route));
const catalogRoute = createRoute({ getParentRoute: () => rootRoute, path: "/catalog" }).lazy(() => import("./routes/catalog").then((m) => m.Route));
const catalogSettingsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/catalog/settings" }).lazy(() => import("./routes/catalogSettings").then((m) => m.Route));
const subscriptionsRoute = createRoute({ getParentRoute: () => rootRoute, path: "/subscriptions" }).lazy(() => import("./routes/subscriptions").then((m) => m.Route));
const graniteRoute = createRoute({ getParentRoute: () => rootRoute, path: "/granite" }).lazy(() => import("./routes/granite").then((m) => m.Route));
const usageRoute = createRoute({ getParentRoute: () => rootRoute, path: "/usage" }).lazy(() => import("./routes/usage").then((m) => m.Route));
const activityRoute = createRoute({ getParentRoute: () => rootRoute, path: "/activity" }).lazy(() => import("./routes/activity").then((m) => m.Route));
const helpRoute = createRoute({ getParentRoute: () => rootRoute, path: "/help" }).lazy(() => import("./routes/help").then((m) => m.Route));

const routeTree = rootRoute.addChildren([
  homeRoute,
  catalogRoute,
  catalogSettingsRoute,
  subscriptionsRoute,
  graniteRoute,
  usageRoute,
  activityRoute,
  helpRoute,
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPendingComponent: Pending,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
